import { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, TrendingUp, TrendingDown } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const navItems = [
  { path: "/about", label: "About" },
  { path: "/products", label: "Products" },
  { path: "/corporate", label: "Corporate Notes" },
  { path: "/specialty", label: "Coming Soon" },
  { path: "/contact", label: "Contact" },
];

export const Navigation = () => {
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [metalPrices, setMetalPrices] = useState({ gold: null, silver: null });
  const [prevPrices, setPrevPrices] = useState({ gold: null, silver: null });
  const [pricesLoading, setPricesLoading] = useState(true);
  const intervalRef = useRef(null);

  useEffect(() => {
    const fetchPrices = async () => {
      try {
        const response = await axios.get(`${API}/metal-prices?t=${Date.now()}`);
        setPrevPrices(metalPrices);
        setMetalPrices({
          gold: response.data.gold,
          silver: response.data.silver
        });
        setPricesLoading(false);
      } catch (error) {
        console.error("Failed to fetch metal prices:", error);
      }
    };

    // Initial fetch
    fetchPrices();
    
    // Refresh every second for live prices
    intervalRef.current = setInterval(fetchPrices, 1000);
    
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  const formatPrice = (price) => {
    if (!price) return "---";
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(price);
  };

  const getPriceDirection = (current, previous) => {
    if (!current || !previous) return null;
    if (current > previous) return 'up';
    if (current < previous) return 'down';
    return null;
  };

  const goldDirection = getPriceDirection(metalPrices.gold, prevPrices.gold);
  const silverDirection = getPriceDirection(metalPrices.silver, prevPrices.silver);

  return (
    <nav 
      className="bg-black/95 backdrop-blur-md"
      data-testid="main-navigation"
    >
      {/* Subtle gold line at top */}
      <div className="h-[1px] bg-gradient-to-r from-transparent via-[#D4AF37]/30 to-transparent" />
      
      <div className="container mx-auto px-4 md:px-8">
        <div className="flex items-center justify-between h-14 md:h-16 py-2">
          {/* Logo */}
          <Link 
            to="/" 
            className="flex items-center group shrink-0"
            data-testid="logo-link"
          >
            <img 
              src="/images/goldnote-logo.png" 
              alt="Goldnote" 
              loading="eager"
              decoding="async"
              className="h-8 w-auto transition-transform duration-300 group-hover:scale-105"
            />
          </Link>

          {/* Desktop Navigation - Centered */}
          <div className="hidden md:flex items-center gap-12">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`relative text-[0.7rem] tracking-[0.2em] uppercase font-light transition-all duration-300 ${
                  location.pathname === item.path 
                    ? 'text-[#D4AF37]' 
                    : 'text-white/80 hover:text-white'
                }`}
                data-testid={`nav-${item.path === "/" ? "home" : item.path.slice(1)}`}
              >
                {item.label}
                {location.pathname === item.path && (
                  <span className="absolute -bottom-2 left-0 right-0 h-[1px] bg-[#D4AF37]" />
                )}
              </Link>
            ))}
          </div>

          {/* Live Metal Prices Ticker */}
          <div className="hidden lg:flex items-center gap-4" data-testid="metal-prices">
            {/* Gold Price */}
            <div className="flex items-center gap-2">
              <span className="text-[0.6rem] tracking-widest uppercase text-[#D4AF37]/70 font-medium">Au</span>
              <span className={`text-sm font-light tabular-nums transition-colors duration-300 ${
                pricesLoading ? 'text-white/30' : 
                goldDirection === 'up' ? 'text-green-400' :
                goldDirection === 'down' ? 'text-red-400' :
                'text-[#D4AF37]'
              }`}>
                {pricesLoading ? '---' : formatPrice(metalPrices.gold)}
              </span>
              {goldDirection === 'up' && <TrendingUp size={10} className="text-green-400" />}
              {goldDirection === 'down' && <TrendingDown size={10} className="text-red-400" />}
            </div>
            
            <div className="w-[1px] h-4 bg-white/10" />
            
            {/* Silver Price */}
            <div className="flex items-center gap-2">
              <span className="text-[0.6rem] tracking-widest uppercase text-white/40 font-medium">Ag</span>
              <span className={`text-sm font-light tabular-nums transition-colors duration-300 ${
                pricesLoading ? 'text-white/30' : 
                silverDirection === 'up' ? 'text-green-400' :
                silverDirection === 'down' ? 'text-red-400' :
                'text-white/70'
              }`}>
                {pricesLoading ? '---' : formatPrice(metalPrices.silver)}
              </span>
              {silverDirection === 'up' && <TrendingUp size={10} className="text-green-400" />}
              {silverDirection === 'down' && <TrendingDown size={10} className="text-red-400" />}
            </div>
            
            <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse ml-1" title="Live" />
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden text-white/70 hover:text-[#D4AF37] transition-colors p-2"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>
      
      {/* Bottom border */}
      <div className="h-[1px] bg-gradient-to-r from-transparent via-white/5 to-transparent" />

      {/* Mobile Navigation */}
      {mobileMenuOpen && (
        <div 
          className="md:hidden absolute top-full left-0 right-0 bg-black/95 backdrop-blur-md border-t border-white/5 py-8 animate-fade-in"
          data-testid="mobile-menu"
        >
          <div className="flex flex-col items-center gap-6">
            {/* Mobile Live Metal Prices */}
            <div className="flex items-center gap-4 mb-2">
              <div className="flex items-center gap-2">
                <span className="text-[0.6rem] tracking-widest uppercase text-[#D4AF37]/70">Au</span>
                <span className={`text-sm tabular-nums ${
                  goldDirection === 'up' ? 'text-green-400' :
                  goldDirection === 'down' ? 'text-red-400' :
                  'text-[#D4AF37]'
                }`}>
                  {formatPrice(metalPrices.gold)}
                </span>
              </div>
              <div className="w-[1px] h-4 bg-white/10" />
              <div className="flex items-center gap-2">
                <span className="text-[0.6rem] tracking-widest uppercase text-white/40">Ag</span>
                <span className={`text-sm tabular-nums ${
                  silverDirection === 'up' ? 'text-green-400' :
                  silverDirection === 'down' ? 'text-red-400' :
                  'text-white/70'
                }`}>
                  {formatPrice(metalPrices.silver)}
                </span>
              </div>
              <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
            </div>
            
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`text-[0.75rem] tracking-[0.15em] uppercase font-light transition-colors ${
                  location.pathname === item.path ? 'text-[#D4AF37]' : 'text-white/80'
                }`}
                onClick={() => setMobileMenuOpen(false)}
              >
                {item.label}
              </Link>
            ))}
          </div>
        </div>
      )}
    </nav>
  );
};
